import type { Identifier } from "ra-core";
import type { AttachmentNote } from "@/components/atomic-crm/types";
import { parseTaskMentionSegments } from "@/components/atomic-crm/tasks/taskMentions";

/**
 * Note threads: a top-level contact or project note plus its replies
 * (notes with parent_id). The same thread shows on the project, the account
 * and the contact; nothing is copied.
 */

export type ThreadResource = "contact_notes" | "deal_notes";

export type ThreadNoteRow = {
  id: Identifier;
  text?: string | null;
  date: string;
  organization_member_id?: Identifier | null;
  parent_id?: Identifier | null;
  /** Reply that this reply answers (nested); null = answers the thread. */
  reply_to_id?: Identifier | null;
  contact_id?: Identifier | null;
  deal_id?: Identifier | null;
  attachments?: AttachmentNote[] | null;
  /** Thread pinned on top (roots only). */
  pinned_at?: string | null;
  pinned_by?: Identifier | null;
  /** Thread marked done (roots only). */
  resolved_at?: string | null;
  resolved_by?: Identifier | null;
  /** Stamped by the database when the text changes. */
  edited_at?: string | null;
  edited_by?: Identifier | null;
};

export type ThreadNote = ThreadNoteRow & { resource: ThreadResource };

export type Thread = {
  root: ThreadNote;
  replies: ThreadNote[];
  /** Newest of root and replies (sort key). */
  lastActivityAt: string;
};

/** Stable key for URLs (`?thread=`) and React lists. */
export const threadNoteKey = (note: Pick<ThreadNote, "resource" | "id">) =>
  `${note.resource}:${note.id}`;

export const parseThreadKey = (
  key?: string | null,
): { resource: ThreadResource; id: string } | null => {
  const match = key?.match(/^(contact_notes|deal_notes):(\d+)$/);
  return match ? { resource: match[1] as ThreadResource, id: match[2] } : null;
};

const tag =
  (resource: ThreadResource) =>
  (row: ThreadNoteRow): ThreadNote => ({ ...row, resource });

/** Groups roots with their replies (oldest reply first). */
export const buildThreads = (params: {
  contactRoots: ThreadNoteRow[];
  dealRoots: ThreadNoteRow[];
  contactReplies: ThreadNoteRow[];
  dealReplies: ThreadNoteRow[];
}): Thread[] => {
  const roots = [
    ...params.contactRoots.map(tag("contact_notes")),
    ...params.dealRoots.map(tag("deal_notes")),
  ];
  const repliesByRoot = new Map<string, ThreadNote[]>();
  const addReplies = (rows: ThreadNoteRow[], resource: ThreadResource) => {
    for (const row of rows) {
      if (row.parent_id == null) continue;
      const key = `${resource}:${row.parent_id}`;
      const list = repliesByRoot.get(key) ?? [];
      list.push({ ...row, resource });
      repliesByRoot.set(key, list);
    }
  };
  addReplies(params.contactReplies, "contact_notes");
  addReplies(params.dealReplies, "deal_notes");

  return roots.map((root) => {
    const replies = (repliesByRoot.get(threadNoteKey(root)) ?? []).sort(
      (a, b) => a.date.localeCompare(b.date),
    );
    const last = replies.at(-1)?.date ?? root.date;
    return {
      root,
      replies,
      lastActivityAt: last > root.date ? last : root.date,
    };
  });
};

/** Plain text of a note (mentions as @Name) for search and previews. */
export const threadPlainText = (text?: string | null) =>
  parseTaskMentionSegments(text ?? "")
    .map((segment) =>
      segment.type === "text" ? segment.value : `@${segment.name}`,
    )
    .join("");

export type ThreadFilter =
  | "all"
  | "open"
  | "resolved"
  | "mine"
  | "files"
  | "project"
  | "general";

export const THREAD_FILTERS: ReadonlyArray<{
  id: ThreadFilter;
  label: string;
}> = [
  { id: "all", label: "All" },
  { id: "mine", label: "Mentions me" },
  { id: "files", label: "With files" },
  { id: "project", label: "Projects" },
  { id: "general", label: "General" },
];

const mentions = (text: string | null | undefined, memberId: Identifier) =>
  parseTaskMentionSegments(text ?? "").some(
    (segment) =>
      segment.type !== "text" && String(segment.id) === String(memberId),
  );

export const filterThreads = (
  threads: Thread[],
  params: {
    filter: ThreadFilter;
    search: string;
    memberId?: Identifier | null;
    /** Extra searchable text per thread (project / person names). */
    labels?: (thread: Thread) => string;
  },
) => {
  const query = params.search.trim().toLowerCase();
  return threads.filter((thread) => {
    const notes = [thread.root, ...thread.replies];
    if (params.filter === "open") {
      if (thread.root.resolved_at) return false;
    } else if (params.filter === "resolved") {
      if (!thread.root.resolved_at) return false;
    } else if (params.filter === "files") {
      if (!notes.some((note) => (note.attachments?.length ?? 0) > 0))
        return false;
    } else if (params.filter === "project") {
      if (thread.root.resource !== "deal_notes") return false;
    } else if (params.filter === "general") {
      if (thread.root.resource !== "contact_notes") return false;
    } else if (params.filter === "mine") {
      if (
        params.memberId == null ||
        !notes.some((note) => mentions(note.text, params.memberId!))
      )
        return false;
    }
    if (!query) return true;
    const haystack = [
      ...notes.map((note) => threadPlainText(note.text)),
      params.labels?.(thread) ?? "",
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
};

export type ThreadSort = "newest" | "oldest" | "activity";

export const sortThreads = (threads: Thread[], sort: ThreadSort) =>
  [...threads].sort((a, b) =>
    sort === "oldest"
      ? a.root.date.localeCompare(b.root.date)
      : sort === "activity"
        ? b.lastActivityAt.localeCompare(a.lastActivityAt)
        : b.root.date.localeCompare(a.root.date),
  );

/** Pinned threads first (newest pin on top), then the rest in order. */
export const splitPinnedThreads = (threads: Thread[]) => {
  const pinned = threads
    .filter((thread) => thread.root.pinned_at)
    .sort((a, b) =>
      String(b.root.pinned_at).localeCompare(String(a.root.pinned_at)),
    );
  return { pinned, rest: threads.filter((thread) => !thread.root.pinned_at) };
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/** "Today", "Yesterday", "Monday" (this week) or "Friday, June 14". */
export const threadDayLabel = (value: string, now = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const days = Math.round(
    (startOfDay(now).getTime() - startOfDay(date).getTime()) / 86_400_000,
  );
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days > 1 && days < 7) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}),
  });
};

/** Groups items under day separators, keeping their order. */
export const groupByDay = <T>(
  items: T[],
  dateOf: (item: T) => string,
  now = new Date(),
) => {
  const groups: Array<{ label: string; items: T[] }> = [];
  for (const item of items) {
    const label = threadDayLabel(dateOf(item), now);
    const last = groups.at(-1);
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
};

/**
 * Consecutive replies by the same person within 5 minutes show one header
 * (avatar, name, time), like a chat.
 */
export const isContinuationReply = (
  previous: Pick<ThreadNote, "organization_member_id" | "date"> | undefined,
  current: Pick<ThreadNote, "organization_member_id" | "date">,
) =>
  previous != null &&
  String(previous.organization_member_id) ===
    String(current.organization_member_id) &&
  Math.abs(
    new Date(current.date).getTime() - new Date(previous.date).getTime(),
  ) <=
    5 * 60_000;

export type ReplyNode = {
  note: ThreadNote;
  children: ReplyNode[];
  depth: number;
};

/**
 * Nests replies under the reply they answer (reply_to_id), oldest first.
 * Replies to a deleted or unknown reply go back to the thread level.
 */
export const buildReplyTree = (replies: ThreadNote[]): ReplyNode[] => {
  const byId = new Map<string, ReplyNode>();
  for (const note of replies) {
    byId.set(String(note.id), { note, children: [], depth: 0 });
  }
  const top: ReplyNode[] = [];
  for (const note of [...replies].sort((a, b) =>
    a.date.localeCompare(b.date),
  )) {
    const node = byId.get(String(note.id))!;
    const parent =
      note.reply_to_id != null ? byId.get(String(note.reply_to_id)) : undefined;
    if (parent && parent !== node) parent.children.push(node);
    else top.push(node);
  }
  const setDepth = (nodes: ReplyNode[], depth: number) => {
    for (const node of nodes) {
      node.depth = depth;
      setDepth(node.children, depth + 1);
    }
  };
  setDepth(top, 0);
  return top;
};
